/**
 * Line-by-line transcript viewer with TX/RX filtering.
 *
 * Radisys provisioning pastes its whole CLI block atomically, so the
 * transcript stores ONE `command` row (the full block) and ONE
 * `response` row (all output). That recording shape is deliberate —
 * the `[y/n]` confirmation alignment and the disconnect-drain capture
 * depend on it — so this component reconstructs the v1-style
 * line-by-line view at render time instead: each row's content is
 * split on newlines, every line gets its own TX / RX / ERR badge, and
 * the toolbar filters by direction.
 */
import { useMemo, useState } from "react";

import { formatLocalTime } from "../../lib/datetime";

export type TranscriptRow = {
  id: number;
  ts: string;
  /** command | response | error | status */
  direction: string;
  content: string;
  byte_len?: number | null;
  truncated?: boolean;
};

type Filter = "all" | "tx" | "rx" | "err";

type DirMeta = { label: string; border: string; text: string; badge: string };

const DIR_META: Record<string, DirMeta> = {
  command: {
    label: "TX",
    border: "border-emerald-700",
    text: "text-emerald-200",
    badge: "border-emerald-800 bg-emerald-900/50 text-emerald-300",
  },
  response: {
    label: "RX",
    border: "border-slate-700",
    text: "text-slate-300",
    badge: "border-slate-700 bg-slate-800/80 text-slate-400",
  },
  error: {
    label: "ERR",
    border: "border-red-700",
    text: "text-red-200",
    badge: "border-red-800 bg-red-900/50 text-red-300",
  },
  status: {
    label: "ST",
    border: "border-amber-700",
    text: "text-amber-200",
    badge: "border-amber-800 bg-amber-900/50 text-amber-300",
  },
};

const FALLBACK_META: DirMeta = DIR_META.status;

function matchesFilter(direction: string, f: Filter): boolean {
  if (f === "all") return true;
  if (f === "tx") return direction === "command";
  if (f === "rx") return direction === "response";
  return direction === "error" || direction === "status";
}

type Line = {
  key: string;
  row: TranscriptRow;
  text: string;
  /** First line of its source row — carries the timestamp + byte badge. */
  first: boolean;
};

export function TranscriptLines({
  rows,
  maxHeightClass = "max-h-[60vh]",
}: {
  rows: TranscriptRow[];
  maxHeightClass?: string;
}) {
  const [filter, setFilter] = useState<Filter>("all");

  // Explode multi-line rows (block pastes / full outputs) into one
  // entry per line so the interaction reads line-by-line like v1.
  const lines = useMemo<Line[]>(() => {
    const out: Line[] = [];
    for (const row of rows) {
      row.content.split("\n").forEach((text, i) => {
        out.push({ key: `${row.id}:${i}`, row, text, first: i === 0 });
      });
    }
    return out;
  }, [rows]);

  const counts = useMemo(() => {
    let tx = 0;
    let rx = 0;
    let err = 0;
    for (const l of lines) {
      if (l.row.direction === "command") tx += 1;
      else if (l.row.direction === "response") rx += 1;
      else err += 1;
    }
    return { all: lines.length, tx, rx, err };
  }, [lines]);

  const visible = useMemo(
    () => lines.filter((l) => matchesFilter(l.row.direction, filter)),
    [lines, filter],
  );

  const pill = (f: Filter, label: string, n: number) => (
    <button
      key={f}
      type="button"
      onClick={() => setFilter(f)}
      className={[
        "rounded border px-2 py-0.5 text-[11px]",
        filter === f
          ? "border-emerald-700 bg-emerald-900/40 text-emerald-200"
          : "border-slate-700 text-slate-400 hover:bg-slate-800",
      ].join(" ")}
    >
      {label} <span className="opacity-60">({n})</span>
    </button>
  );

  if (rows.length === 0) {
    return <p className="text-xs text-slate-500">No transcript rows.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {pill("all", "All", counts.all)}
        {pill("tx", "TX — sent", counts.tx)}
        {pill("rx", "RX — received", counts.rx)}
        {pill("err", "Errors / status", counts.err)}
      </div>
      {visible.length === 0 ? (
        <p className="text-xs text-slate-500">
          No lines match this filter.
        </p>
      ) : (
        <ol
          className={[
            "flex flex-col overflow-auto font-mono text-[11px]",
            maxHeightClass,
          ].join(" ")}
        >
          {visible.map((l) => {
            const meta = DIR_META[l.row.direction] ?? FALLBACK_META;
            return (
              <li
                key={l.key}
                className={[
                  "flex items-baseline gap-2 border-l-2 px-2 py-px",
                  meta.border,
                  meta.text,
                ].join(" ")}
              >
                <span
                  className={[
                    "inline-flex w-8 shrink-0 justify-center rounded border",
                    "text-[9px] uppercase tracking-wide",
                    meta.badge,
                  ].join(" ")}
                >
                  {meta.label}
                </span>
                {l.first && (
                  <span className="shrink-0 text-[10px] text-slate-500">
                    {formatLocalTime(l.row.ts)}
                    {l.row.truncated && l.row.byte_len ? (
                      <span className="ml-1 text-amber-400">
                        [{l.row.byte_len} bytes, truncated]
                      </span>
                    ) : null}
                  </span>
                )}
                <span className="whitespace-pre-wrap break-all">
                  {l.text || " "}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
